import { ExclamationMarkIcon } from '@/icons/ExclamationMarkIcon';
import {
    getPOSProviders,
    useCurrentCompanyDetails,
    useSalaryProvidersQuery,
    useTimeRegistrationProvidersQuery
} from '@/services/paymentsQuery';

// The secondary data source is whichever system isn't the primary one: when the
// company drives off the time-registration system, salary is secondary, and vice
// versa. Returns the provider name (e.g. 'Planday'), or undefined when unknown.
export const getSecondaryProviderName = (company?: {
    dataPrimarySource?: string;
    salaryProvider?: string;
    timeRegistrationProvider?: string;
}): string | undefined =>
    company?.dataPrimarySource === 'TimeRegistrationSystem'
        ? company?.salaryProvider
        : company?.timeRegistrationProvider;

export const useSalaryProviders = () => {
    'use no memo';
    const { data: getSalaryProvidersQuery } = useSalaryProvidersQuery();

    const getIconForTimeSalaryProvider = (
        fileUrl?: string,
        size = 'normal'
    ) => {
        if (fileUrl) {
            return (
                <img
                    style={{
                        borderRadius: 50,
                        alignSelf: 'center',
                        margin: '0 auto'
                    }}
                    width={size == 'small' ? 24 : 40}
                    height={size == 'small' ? 24 : 40}
                    src={fileUrl}
                    alt={'logo'}
                />
            );
        }

        return <ExclamationMarkIcon />;
    };

    const getLogoForSalaryProvider = (
        providerName?: string,
        size = 'normal'
    ) => {
        let found = getSalaryProvidersQuery?.data?.salaryProviders?.find(
            (el) => el.name == providerName
        );
        return getIconForTimeSalaryProvider(found?.fileUrl, size);
    };

    return {
        getSalaryProvidersQuery,
        getLogoForSalaryProvider,
        getIconForTimeSalaryProvider
    };
};

export const useTimeProviders = () => {
    'use no memo';
    const getTimeRegistrationProvidersQuery =
        useTimeRegistrationProvidersQuery();

    const getIconForTimeRegistrationProvider = (
        fileUrl?: string,
        size = 'normal'
    ) => {
        if (fileUrl) {
            return (
                <img
                    style={{
                        borderRadius: 50,
                        alignSelf: 'center',
                        margin: '0 auto'
                    }}
                    width={size == 'small' ? 24 : 40}
                    height={size == 'small' ? 24 : 40}
                    src={fileUrl}
                    alt={'logo'}
                />
            );
        }

        return <ExclamationMarkIcon />;
    };

    const getLogoForTimeRegistrationProvider = (
        providerName?: string,
        size = 'normal'
    ) => {
        let found =
            getTimeRegistrationProvidersQuery?.data?.data?.providers?.find(
                (el) => el.name == providerName
            );
        return getIconForTimeRegistrationProvider(found?.fileUrl, size);
    };

    return {
        getTimeRegistrationProvidersQuery,
        getLogoForTimeRegistrationProvider,
        getIconForTimeRegistrationProvider
    };
};

export const usePOSProvider = () => {
    'use no memo';
    const { data, isSuccess } = getPOSProviders();
    const { data: companyDetails } = useCurrentCompanyDetails();
    const getIconPOSProvider = (fileUrl?: string | null, size = 'normal') => {
        if (fileUrl) {
            return (
                <img
                    style={{
                        borderRadius: 50,
                        alignSelf: 'center',
                        margin: '0 auto'
                    }}
                    width={size == 'small' ? 24 : 40}
                    height={size == 'small' ? 24 : 40}
                    src={fileUrl}
                    alt={'logo'}
                />
            );
        }

        return <ExclamationMarkIcon />;
    };

    const getLogoForPOSProvider = () => {
        if (data?.data?.length ?? 0 > 0) {
            return getIconPOSProvider(companyDetails?.data?.posProviderFileUrl);
        }
    };

    return {
        getLogoForPOSProvider,
        posProviderName: companyDetails?.data?.posProvider,
        posDataSuccess: isSuccess,
        posProviders: data?.data
    };
};
